import { useState, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowLeft, Calendar, Clock } from 'lucide-react'
import { supabase } from '../config/supabase'

export default function NewsDetailPage() {
  const [news, setNews] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        // 获取 URL 中的新闻 ID
        const urlParams = new URLSearchParams(window.location.search)
        const newsId = urlParams.get('id')

        if (newsId) {
          // 查询新闻详情
          const { data: newsData, error } = await supabase
            .from('news_s_762b1c2b_1')
            .select('*')
            .eq('id', newsId)
            .eq('is_deleted', 'n')
            .single()

          if (error) {
            console.error('Error fetching news detail:', error)
          } else {
            // 为新闻添加默认属性
            const enhancedNews = {
              ...newsData,
              id: newsData.id,
              title: newsData.title || '新闻标题',
              date: newsData.date || newsData.created_at ? new Date(newsData.created_at).toISOString().split('T')[0] : '2024-01-01',
              time: newsData.time || '12:00',
              category: newsData.category || '公司动态',
              image: newsData.image || newsData.cover_image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(newsData.title || 'news article')}&image_size=landscape_4_3`,
              content: newsData.content || '新闻详细内容...'
            }
            setNews(enhancedNews)
          }
        }
      } catch (error) {
        console.error('Error fetching news detail:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchNewsDetail()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-secondary-600 text-lg">加载中...</p>
        </div>
        <Footer />
      </div>
    )
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-secondary-600 text-lg">新闻不存在</p>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <a 
            href="/news" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            返回新闻列表
          </a>
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              {news.category}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              {news.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-white/80">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{news.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{news.time}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* News Image */}
          <div className="mb-8">
            <img
              src={news.image}
              alt={news.title}
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </div>

          {/* News Content */}
          <div className="news-content">
            <div dangerouslySetInnerHTML={{ __html: news.content }} />
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}
